import { useState, type FormEvent } from "react";
import { useMutation } from "@connectrpc/connect-query";
import { Link, useLocation } from "react-router-dom";

import {
  loginWithPassword,
  requestLoginCode,
  verifyLoginCode,
} from "@/gen/nodebase/v1/account-AccountService_connectquery";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ErrorNote } from "@/components/page";
import { isRegistrationRequired } from "@/lib/format";
import { deviceReturnPath } from "@/routes/device";
import {
  forgetRememberedAccount,
  getRememberedEmail,
  sessionFrom,
  setSession,
} from "@/lib/session";

export function Login() {
  // Signed out on the device approval page, this card stands in front of it:
  // once a session exists App renders the page at the same URL, and the
  // registration link carries the way back.
  const location = useLocation();
  const forDevice = deviceReturnPath(location.pathname + location.search);
  const registerTo = forDevice
    ? `/register?next=${encodeURIComponent(forDevice)}`
    : "/register";
  const [remembered, setRemembered] = useState(getRememberedEmail);
  const [step, setStep] = useState<"email" | "code">("email");
  const [email, setEmail] = useState(remembered);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [code, setCode] = useState("");
  const [registrationRequired, setRegistrationRequired] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const requestCode = useMutation(requestLoginCode);
  const verify = useMutation(verifyLoginCode);
  const withPassword = useMutation(loginWithPassword);
  const busy =
    requestCode.isPending || verify.isPending || withPassword.isPending;

  async function onSubmitEmail(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const addr = email.trim();
    try {
      // Operator path: accounts the operator seeded a password for (control-api
      // set-password) skip the mailed code, because their mailbox often cannot
      // receive one. A blank password means the ordinary product login.
      if (password) {
        const res = await withPassword.mutateAsync({
          email: addr,
          password,
        });
        setPassword("");
        setSession(sessionFrom(addr, res.accountId, res.session));
        return;
      }
      await requestCode.mutateAsync({ email: addr });
      setStep("code");
    } catch (err) {
      setError(err);
    }
  }

  async function onSubmitCode(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const addr = email.trim();
    try {
      const res = await verify.mutateAsync({
        email: addr,
        code: code.trim(),
      });
      setSession(sessionFrom(addr, res.accountId, res.session));
    } catch (err) {
      setRegistrationRequired(isRegistrationRequired(err));
      setError(err);
    }
  }

  return (
    <div className="flex min-h-dvh items-center justify-center bg-background px-4">
      <Card className="w-full max-w-sm" data-testid="login-card">
        <CardHeader>
          <CardTitle>Sign in to Nodebase</CardTitle>
          <CardDescription>
            {step === "email"
              ? forDevice
                ? "A device is waiting for you to sign in. Enter your email and we'll send you a 6-digit code."
                : "Enter your email and we'll send you a 6-digit code."
              : `Enter the code sent to ${email.trim()}.`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ErrorNote error={error} />

          {step === "email" ? (
            <form className="space-y-4" onSubmit={onSubmitEmail}>
              <div className="space-y-1.5">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="username"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                {remembered && (
                  <div className="flex flex-col items-start gap-1 pt-1">
                    <p className="text-xs leading-5 text-muted-foreground">
                      Account remembered on this device
                    </p>
                    <Button
                      type="button"
                      variant="link"
                      size="sm"
                      className="h-auto min-h-6 px-0 py-0 font-normal text-muted-foreground hover:text-foreground"
                      onClick={() => {
                        forgetRememberedAccount();
                        setRemembered("");
                        setEmail("");
                      }}
                    >
                      Forget this account
                    </Button>
                  </div>
                )}
              </div>
              {showPassword ? (
                <div className="space-y-1.5">
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password"
                    name="password"
                    type="password"
                    autoComplete="current-password"
                    autoFocus
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">
                    For accounts an operator set a password on. Ordinary
                    accounts use the emailed code.
                  </p>
                </div>
              ) : (
                <Button
                  type="button"
                  variant="link"
                  className="h-auto px-0"
                  onClick={() => setShowPassword(true)}
                >
                  Use a password instead
                </Button>
              )}
              <Button type="submit" className="w-full" disabled={busy}>
                {password ? "Sign in" : "Send code"}
              </Button>
              <Button type="button" variant="ghost" className="w-full" disabled={busy}
                onClick={(e) => {
                  if (!e.currentTarget.form?.reportValidity()) return;
                  setError(null);
                  setStep("code");
                }}>
                I already have a code
              </Button>
              <p className="text-center text-sm text-muted-foreground">
                New here?{" "}
                <Link className="underline underline-offset-4" to={registerTo}>
                  Create your account
                </Link>
              </p>
            </form>
          ) : (
            <form className="space-y-4" onSubmit={onSubmitCode}>
              <div className="space-y-1.5">
                <Label htmlFor="code">Login code</Label>
                <Input
                  id="code"
                  name="code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  autoFocus
                  required
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                />
              </div>
              {registrationRequired && (
                <div className="rounded-md border bg-secondary p-3 text-sm" data-testid="registration-required">
                  <p>
                    No Nodebase account was found for this email. Finish
                    registration here, then sign in.
                  </p>
                  <Button asChild variant="link" className="h-auto px-0 pt-2">
                    <Link to={registerTo}>Open registration</Link>
                  </Button>
                </div>
              )}
              <Button type="submit" className="w-full" disabled={busy}>
                Verify
              </Button>
              <Button
                type="button"
                variant="ghost"
                className="w-full"
                onClick={() => {
                  setStep("email");
                  setError(null);
                  setRegistrationRequired(false);
                }}
              >
                Use a different email
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
